import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
// Using CKEditor instead of angular-email-editor for compatibility
// import { EmailEditorComponent } from 'angular-email-editor';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, merge, startWith, switchMap, of as observableOf } from 'rxjs';
import { EmailTemplateService } from 'src/app/private/services/email-tempate.service';
import { UsersService } from 'src/app/private/services/users.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { TrackingService } from 'src/app/private/services/tracking.service';


@Component({
  selector: 'app-email-template',
  templateUrl: './email-template.component.html',
  styleUrls: ['./email-template.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class EmailTemplateComponent {


  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  totalRows = 0;
  currentPage = 0;
  pageSize = 25;
  pageSizeOptions: number[] = [10, 25, 100];
  @ViewChild(MatPaginator)
  paginator!: MatPaginator;
  @ViewChild(MatSort)
  sort!: MatSort;
  searchQuery = new FormControl();
  dateQuery = new FormControl();
  btnLoading = false;
  initialLoad = true;
  processedData: any;
  processedAttachments: any[] = [];
  templateType: string = 'html';  // 'text' or 'html'
  templateToDelete: any;
  templateEdit: any;
  isAdmin: boolean = false; // Initialize isAdmin to false

  displayedColumns: string[] = [
    // "Client_ID",
    "Template_Name",
    "Template_Category",
    "Template_Body",
    "Status",
    "Action"
  ];

  constructor(
    private toast: ToastrService,
    private api: EmailTemplateService,
    private modalService: NgbModal,
    private localStorage: LocalStorageService,
    private userApi: UsersService,
    private sanitizer: DomSanitizer,
    private tracker: TrackingService
  ) {

    this.isAdmin = this.localStorage.checkFullAccessForTableList(
      this.localStorage.getRole()
    );
  }

  ngAfterViewInit() {
    this.loadData();
  }


  loadData() {

    try {
      this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
      merge(
        this.dateQuery.valueChanges,
        this.searchQuery.valueChanges,
        this.sort.sortChange,
        this.paginator.page
      )
        .pipe(
          startWith({}),
          switchMap(() => {

            let param: any = {
              where: {
                "hospital_id": this.localStorage.getHospitalId()
              },
              "page": this.paginator.pageIndex * this.paginator.pageSize,
              "limit": this.paginator.pageSize,
            }

            if (!this.isAdmin) {
              param.where.user_id = this.localStorage.getUserId();
            }

            return this.api
              .getAllTemplate(param)
              .pipe(catchError(() => {
                return observableOf([]);
              }
              ));
          }),
          map((response: any) => {
            if (response === null) {
              return [];
            }
            this.totalRows = response.count;
            return response.rows;
          })
        )
        .subscribe({
          next: (data) => {
            this.initialLoad = false;
            this.dataSource = new MatTableDataSource<any>(data);
          },
          error: (e) => {
            this.initialLoad = false;
            console.error(e);
          },
          complete: () => {
          },
        });
    } catch (e) {
      console.error(e);
    }
  }

  handleUpdateData(type?: string) {
    try {

      let param: any = {
        where: {
          "hospital_id": this.localStorage.getHospitalId()
        },
        "page": this.paginator.pageIndex * this.paginator.pageSize,
        "limit": this.paginator.pageSize,
      }

      if (!this.isAdmin) {
        param.where.user_id = this.localStorage.getUserId();
      }

      this.api.getAllTemplate(param).subscribe(
        {
          next: (response) => {
            if (response) {
              this.totalRows = response.count;
              this.dataSource = new MatTableDataSource<any>(response.rows);
            }

            if (type === "delete") {
              this.toast.success("Template deleted successfully");
              this.tracker.track("Delete", "Email Template", `Deleted template: ${this.templateToDelete?.template_name}`);
            }

            if (type === "Update") {
              this.toast.success("Template updated successfully");
              this.tracker.track("Update", "Email Template", `Updated template: ${this.templateEdit?.template_name}`);
            }

            if (type === "Create") {
              this.toast.success("Template added successfully");
              this.tracker.track("Create", "Email Template", `Created new template`);

            }

            this.btnLoading = false

            this.modalService.dismissAll();
          },
          error: (e) => {
            console.error(e);
          },
          complete: () => {
          },
        }
      )

    } catch (e) {
      console.error(e);
    }
  }


  open(content: any) {
    this.modalService.open(content, {
      // size: 'xl',
      fullscreen: true,
      scrollable: true,
      centered: true,
      windowClass: 'my-class',
      backdrop: 'static',   // ❌ Disable close on outside click
      keyboard: false       // ❌ Disable close on ESC key (optional but recommended)
    });
  }

  handleOpenPreviewDialog(content: any, template: any) {
    this.modalService.open(content, { centered: true, size: 'lg', backdrop: 'static', keyboard: false });
    
    this.templateType = template.template_type || 'html';  // Default to html if not specified
    // Always pass raw string — preview component wraps it in a full HTML document
    this.processedData = template.template_body;

    // Process attachments if available
    if (template.attachments && Array.isArray(template.attachments)) {
      this.processedAttachments = template.attachments;
    } else {
      this.processedAttachments = [];
    }

    this.tracker.track(
      'Preview',
      'Email Template',
      `Previewed template: ${template.template_name}`
    );
  }


  handleOpenEditDialog(content: any, template: any) {
    this.templateEdit = template;
    this.modalService.open(content, { 
      fullscreen: true
      , scrollable: true, centered: true, windowClass: 'my-class', backdrop: 'static', keyboard: false });
    this.tracker.track(
      'Edit',
      'Email Template',
      `Opened edit dialog for template: ${template.template_name}`
    );

  }

  handleDeleteEvent() {

    this.btnLoading = true;

    const payload = {
      template_id: this.templateToDelete.template_id
    }

    this.api.deleteTemplate(payload).subscribe((response: any) => {
      if (response) {
        this.handleUpdateData("delete");
      }
    },
      (error: any) => {
        this.btnLoading = false;
        this.toast.error("Something went wrong");
      });

  }

  handleOpenDeleteTemplateDialog(content: any, element: any) {
    this.modalService
      .open(content, { size: 'md', centered: true, backdrop: 'static', keyboard: false })

    this.templateToDelete = element;
    this.tracker.track(
      'Open Delete Modal',
      'Email Template',
      `Opened delete dialog for template: ${element.template_name}`
    );
  }


}